/**
 * useSubmission Hook - Orchestrates the submission flow
 *
 * Handles:
 * - Ephemeral key generation
 * - Form data encryption (V1 and V2 - authority-based)
 * - Submission to backend
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createStoredKeyPair,
  encryptForRecipients,
  encryptForAuthority,
  StoredKeyPair,
  EncryptionRecipient,
} from '@antragsmanager/crypto-lib';
import { submitEncryptedApplication } from '../api/citizenApi';
import { AuthorityPublicKeyInfo, SubmissionResponse } from '../api/types';

/**
 * Authority info for V2 encryption (multi-employee system)
 */
interface AuthorityKeyInfo {
  authorityId: string;
  authorityName: string;
  publicKeyJwk: JsonWebKey;
  fingerprint: string;
  currentEpoch: number;
}

interface UseSubmissionResult {
  /** Whether a submission is in progress */
  isSubmitting: boolean;

  /** Error message if submission failed */
  error: string | null;

  /** The ephemeral key pair (generated when needed) */
  ephemeralKey: StoredKeyPair | null;

  /** Generate a new ephemeral key */
  generateEphemeralKey: () => Promise<StoredKeyPair>;

  /** Submit form data encrypted for authority (V1 - legacy) */
  submitForm: (
    formData: Record<string, unknown>,
    authority: AuthorityPublicKeyInfo,
    authorityKeyId: string,
    processDefinitionKey: string
  ) => Promise<SubmissionResponse>;

  /** Submit form data encrypted for authority (V2 - multi-employee) */
  submitFormV2: (
    formData: Record<string, unknown>,
    authority: AuthorityKeyInfo,
    processDefinitionKey: string,
    additionalNavigationState?: Record<string, unknown>
  ) => Promise<SubmissionResponse>;

  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook for managing the submission process
 */
export function useSubmission(): UseSubmissionResult {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ephemeralKey, setEphemeralKey] = useState<StoredKeyPair | null>(null);

  /**
   * Generate a new ephemeral key pair for this submission
   * This key is NOT stored in IndexedDB - it's only for this session
   */
  const generateEphemeralKey = useCallback(async (): Promise<StoredKeyPair> => {
    const keyPair = await createStoredKeyPair();
    setEphemeralKey(keyPair);
    return keyPair;
  }, []);

  /**
   * Submit form data encrypted for the authority
   */
  const submitForm = useCallback(
    async (
      formData: Record<string, unknown>,
      authority: AuthorityPublicKeyInfo,
      authorityKeyId: string,
      processDefinitionKey: string
    ): Promise<SubmissionResponse> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Ensure we have an ephemeral key
        let senderKey = ephemeralKey;
        if (!senderKey) {
          senderKey = await generateEphemeralKey();
        }

        // Create recipient from authority's public key
        const recipient: EncryptionRecipient = {
          keyId: authority.keyId,
          publicKeyJwk: authority.publicKeyJwk,
        };

        // Encrypt the form data
        const encryptedFormData = await encryptForRecipients(
          formData,
          [recipient],
          senderKey
        );

        // Submit to backend
        const response = await submitEncryptedApplication(authorityKeyId, {
          encryptedFormData,
          processDefinitionKey,
        });

        // Navigate to success page with response data
        navigate(`/submit/${authorityKeyId}/${processDefinitionKey}/success`, {
          state: {
            referenceNumber: response.referenceNumber,
            submittedAt: response.submittedAt,
            authorityName: response.authorityName,
          },
          replace: true,
        });

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [ephemeralKey, generateEphemeralKey, navigate]
  );

  /**
   * Submit form data encrypted for authority using V2 (multi-employee) encryption
   * This uses the authority's AEK for encryption, which can be decrypted by any
   * employee with the GKDK.
   */
  const submitFormV2 = useCallback(
    async (
      formData: Record<string, unknown>,
      authority: AuthorityKeyInfo,
      processDefinitionKey: string,
      additionalNavigationState?: Record<string, unknown>
    ): Promise<SubmissionResponse> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Ensure we have an ephemeral key
        let senderKey = ephemeralKey;
        if (!senderKey) {
          senderKey = await generateEphemeralKey();
        }

        // Encrypt the form data using V2 authority-based encryption
        const encryptedFormData = await encryptForAuthority(
          formData,
          authority.publicKeyJwk,
          authority.authorityId,
          authority.currentEpoch,
          senderKey
        );

        // Submit to backend using the authority ID
        const response = await submitEncryptedApplication(authority.authorityId, {
          encryptedFormData,
          processDefinitionKey,
        });

        // Navigate to success page with response data
        navigate(`/submit/${authority.authorityId}/${processDefinitionKey}/success`, {
          state: {
            referenceNumber: response.referenceNumber,
            submittedAt: response.submittedAt,
            authorityName: response.authorityName || authority.authorityName,
            ...additionalNavigationState,
          },
          replace: true,
        });

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [ephemeralKey, generateEphemeralKey, navigate]
  );

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isSubmitting,
    error,
    ephemeralKey,
    generateEphemeralKey,
    submitForm,
    submitFormV2,
    clearError,
  };
}
